import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import * as CANNON from "cannon-es";
import { useFrame, useThree } from "@react-three/fiber";

function clapTexture() {
  const c = document.createElement("canvas");
  c.width = 128; c.height = 96;
  const g = c.getContext("2d");
  g.fillStyle = "#111"; g.fillRect(0, 0, 128, 96);
  for (let i = 0; i < 4; i++) {
    g.fillStyle = i % 2 ? "#111" : "#eee";
    g.save();
    g.translate(i * 34 - 10, 0);
    g.transform(1, 0, -0.5, 1, 0, 0);
    g.fillRect(0, 0, 18, 26);
    g.restore();
  }
  g.fillStyle = "#eee";
  g.font = "bold 18px Arial";
  g.fillText("SCENA 12", 12, 56);
  g.fillText("TAKE 3", 12, 80);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

const PROPS = [
  { pos: [-2.2, 0.26, 4.6], size: [0.52, 0.52, 0.52], color: "#6b5334" },
  { pos: [-2.2, 0.79, 4.6], size: [0.52, 0.52, 0.52], color: "#75593a" },
  { pos: [-1.6, 0.26, 4.9], size: [0.52, 0.52, 0.52], color: "#5d472e" },
  { pos: [2.4, 0.2, 4.2], size: [0.6, 0.4, 0.4], color: "#3a3d44", rotY: 0.5 },
  { pos: [0.5, 1.05, 2.4], size: [0.34, 0.26, 0.05], clap: true, rotY: -0.5 },
  { pos: [1.8, 0.14, 5.2], size: [0.5, 0.28, 0.35], color: "#2e2a24", rotY: 1.1 },
];

export function PhysicsProps() {
  const { gl, camera } = useThree();
  const world = useRef(null);
  const bodies = useRef([]);
  const meshes = useRef([]);
  const groupRef = useRef(null);

  useEffect(() => {
    const w = new CANNON.World({ gravity: new CANNON.Vec3(0, -9.82, 0) });
    w.broadphase = new CANNON.SAPBroadphase(w);
    world.current = w;
    const ground = new CANNON.Body({ type: CANNON.Body.STATIC, shape: new CANNON.Plane() });
    ground.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    w.addBody(ground);

    PROPS.forEach((p, i) => {
      const [sx, sy, sz] = p.size;
      const body = new CANNON.Body({
        mass: 1.2,
        shape: new CANNON.Box(new CANNON.Vec3(sx / 2, sy / 2, sz / 2)),
        position: new CANNON.Vec3(...p.pos),
        linearDamping: 0.25,
        angularDamping: 0.3,
      });
      body.quaternion.setFromEuler(0, p.rotY || 0, 0);
      w.addBody(body);
      bodies.current[i] = body;
    });

    const raycaster = new THREE.Raycaster();
    const onUp = (e) => {
      const r = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(meshes.current.filter(Boolean));
      if (hits.length) {
        const idx = meshes.current.indexOf(hits[0].object);
        const body = bodies.current[idx];
        if (body) {
          const away = body.position.vsub(new CANNON.Vec3(camera.position.x, camera.position.y, camera.position.z));
          away.y = 0;
          away.normalize();
          body.wakeUp();
          body.applyImpulse(new CANNON.Vec3(away.x * 2.2, 2.6, away.z * 2.2), new CANNON.Vec3(
            body.position.x + (Math.random() - 0.5) * 0.2,
            body.position.y + 0.1,
            body.position.z + (Math.random() - 0.5) * 0.2
          ));
        }
      }
    };
    gl.domElement.addEventListener("pointerup", onUp);
    return () => gl.domElement.removeEventListener("pointerup", onUp);
  }, [gl, camera]);

  useFrame((_, delta) => {
    if (!world.current) return;
    world.current.step(1 / 60, Math.min(delta, 0.05), 3);
    meshes.current.forEach((m, i) => {
      const b = bodies.current[i];
      if (m && b) {
        m.position.copy(b.position);
        m.quaternion.copy(b.quaternion);
      }
    });
  });

  const clap = React.useMemo(clapTexture, []);

  return (
    <group ref={groupRef}>
      {PROPS.map((p, i) => (
        <mesh
          key={i}
          ref={(r) => (meshes.current[i] = r)}
          position={p.pos}
          rotation={[0, p.rotY || 0, 0]}
          castShadow
        >
          <boxGeometry args={p.size} />
          {p.clap ? (
            <meshStandardMaterial map={clap} roughness={0.6} />
          ) : (
            <meshStandardMaterial color={p.color} roughness={0.85} />
          )}
        </mesh>
      ))}
    </group>
  );
}
